import React, { useState, useRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import { IoCalendarOutline, IoAddOutline, IoCloseOutline, IoOptionsOutline } from "react-icons/io5";
import "react-datepicker/dist/react-datepicker.css";
import "./DateFilter.css";

export interface DateRange {
  id: string;
  startDate: Date | null;
  endDate: Date | null;
  selectedYear: number;
  label?: string;
}

export interface DateFilterProps {
  availableYears?: number[];
  onChange?: (ranges: DateRange[]) => void;
  initialRanges?: DateRange[];
  dropdownClassName?: string;
  maxCalendars?: number;
}

const generateId = () => Math.random().toString(36).slice(2, 9);

const currentYear = new Date().getFullYear();
const DEFAULT_YEARS = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);

const DateFilter: React.FC<DateFilterProps> = ({
  availableYears = DEFAULT_YEARS,
  onChange,
  initialRanges,
  dropdownClassName = "",
  maxCalendars = 5,
}) => {
  const [open, setOpen] = useState(false);
  const [ranges, setRanges] = useState<DateRange[]>(
    initialRanges?.map(r => ({
      ...r,
      selectedYear: currentYear,
    })) || [
      {
        id: generateId(),
        startDate: null,
        endDate: null,
        label: "Range 1",
        selectedYear: currentYear,
      },
    ]
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const updateRange = (id: string, patch: Partial<DateRange>) => {
    setRanges((prev) => {
      const updated = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
      onChange?.(updated);
      return updated;
    });
  };

  const addRange = () => {
    if (ranges.length >= maxCalendars) return;

    setRanges((prev) => {
      const updated = [
        ...prev,
        {
          id: generateId(),
          startDate: null,
          endDate: null,
          label: `Range ${prev.length + 1}`,
          selectedYear: currentYear,
        },
      ];
      onChange?.(updated);
      return updated;
    });
  };

  const removeRange = (id: string) => {
    setRanges((prev) => {
      const updated = prev.filter((r) => r.id !== id);
      onChange?.(updated);
      return updated.length === 0
        ? [{
          id: generateId(),
          startDate: null,
          endDate: null,
          label: "Range 1",
          selectedYear: currentYear,
        }]
        : updated;
    });
  };

  const handleReset = () => {
    const reset = [{
      id: generateId(),
      startDate: null,
      endDate: null,
      label: "Range 1",
      selectedYear: currentYear,
    }];
    setRanges(reset);
    onChange?.(reset);
  };

  const activeCount = ranges.filter((r) => r.startDate || r.endDate).length;

  return (
    <div className="df-wrapper" ref={wrapperRef}>
      <button className="df-trigger" onClick={() => setOpen((p) => !p)}>
        <IoCalendarOutline className="df-icon" />
        Date Filter
        {activeCount > 0 && <span className="df-badge">{activeCount}</span>}
      </button>

      {open && (
        <div className={`df-dropdown ${dropdownClassName}`}>
          <div className="df-calendars">
            {ranges.map((range, idx) => (

              <div key={range.id} className="df-calendar-card">
                <div className="df-header">
                  <div className="df-year-tabs">
                    {availableYears.map((yr) => (
                      <button
                        key={yr}
                        className={`df-year-tab ${range.selectedYear === yr ? "active" : ""}`}
                        onClick={() => {
                          const startOfYear = new Date(yr, 0, 1)
                          const endOfYear = new Date(yr, 11, 31)

                          updateRange(range.id, {
                            selectedYear: yr,
                            startDate: startOfYear,
                            endDate: endOfYear,
                          })
                        }}
                      >
                        {yr}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="df-card-header">
                  <input
                    className="df-range-label"
                    value={range.label || `Range ${idx + 1}`}
                    onChange={(e) => updateRange(range.id, { label: e.target.value })}
                    placeholder="Label..."
                  />
                  <button
                    className="df-remove-btn"
                    onClick={() => removeRange(range.id)}
                    title="Remove"
                  >
                    <IoCloseOutline />
                  </button>
                </div>

                <DatePicker
                  selected={range.startDate}
                  onChange={([start, end]) =>
                    updateRange(range.id, { startDate: start, endDate: end })
                  }
                  startDate={range.startDate}
                  endDate={range.endDate}
                  selectsRange
                  inline
                  minDate={new Date(range.selectedYear, 0, 1)}
                  maxDate={new Date(range.selectedYear, 11, 31)}
                  openToDate={
                    range.startDate
                      ? range.startDate
                      : new Date(range.selectedYear, new Date().getMonth(), 1)
                  }
                  calendarClassName="df-calendar"
                />

                {(range.startDate || range.endDate) && (
                  <div className="df-range-summary">
                    <span>{range.startDate ? range.startDate.toLocaleDateString() : "—"}</span>
                    <span className="df-arrow">→</span>
                    <span>{range.endDate ? range.endDate.toLocaleDateString() : "—"}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {ranges.length < maxCalendars && (
            <button className="df-add-btn" onClick={addRange}>
              <IoAddOutline /> Add Date Range
            </button>
          )}

          <div className="df-footer">
            <button className="df-reset-btn" onClick={handleReset}>
              Reset
            </button>
            <button className="df-apply-btn" onClick={() => setOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilter;